import React from "react";
import { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Autocomplete,
  MenuItem,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import Button from "../../../../componentsv2/Button";

import MascaraDeCompetencia from "../../Componentes/MascaraDeCompetencia";
import DataPickerAtualizado from "../../../../components/DataPickerAtualizado";

import { useSnackbar } from "notistack";

import { api } from "../../../../services/Api";

import useUser from "../../../../hooks/useUser";
import {
  montarPayloadLote,
  extrairMensagemErroCriacaoLote,
  salvarProtocoloELote,
  validarCamposObrigatoriosLote,
} from "../utils/lote";
import {
  filtrarConvenios,
  obterLabelConvenio,
} from "../../convenio";

const formatarValorMonetario = (valor) => {
  if (valor === "" || valor === null || valor === undefined) {
    return "";
  }

  const numero = Number(valor);

  return Number.isFinite(numero)
    ? numero.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "";
};

const ModalConstruirLote = ({
  open,
  handleClose,
  listaConvenios = [],
  setAtualizaTabelaFaturamento,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const criarDataInicial = () => {
    const valueDateI = new Date();
    valueDateI.setDate(valueDateI.getDate() - 10);
    return valueDateI;
  };
  const [competencia, setCompetencia] = useState("");
  const [protocolo, setProtocolo] = useState("");
  const [previsao, setPrevisao] = useState(null);
  const [convenio, setConvenio] = useState("");
  const [dataInicial, setDataInicial] = useState(criarDataInicial);
  const [dataFinal, setDataFinal] = useState(new Date());
  const [lote, setLote] = useState("");
  const [valor, setValor] = useState("");
  const [quantidade, setQuantidade] = useState("");
  const [tipo, setTipo] = useState("NORMAL");
  const [loadingSalvarLote, setLoadingSalvarLote] = useState(false);

  const convenioSelecionado =
    listaConvenios.find((item) => item.convenio === convenio) || null;

  const usuario = useUser();

  const ultimoLote = async () => {
    try {
      const loteUnico = await api.get("/v2/faturamento/ultimoLote", {
        params: {
          plano: convenio,
        },
      });
      setLote(loteUnico.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (open && convenio) {
      ultimoLote();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convenio, open]);

  const salvarLote = async (event) => {
    event.preventDefault();

    const mensagemValidacao = validarCamposObrigatoriosLote({
      convenio,
      lote,
      dataInicial,
      dataFinal,
      competencia,
      tipo,
    });

    if (mensagemValidacao) {
      enqueueSnackbar(mensagemValidacao, {
        variant: "warning",
      });
      return;
    }

    const payloadLote = montarPayloadLote({
      dataInicial,
      dataFinal,
      competencia,
      codigo: lote,
      plano: convenio,
      protocolo,
      previsao,
      valor,
      quantidade,
      emConstrucao: true,
      usuarioResponsavel: usuario,
      tipo,
    });

    try {
      setLoadingSalvarLote(true);
      await salvarProtocoloELote({
        payloadLote,
        apiClient: api,
      });
      setAtualizaTabelaFaturamento((prevState) => !prevState);
      enqueueSnackbar("Lote criado com sucesso.", {
        variant: "success",
      });
      handleClose(false);
      limparDados();
    } catch (error) {
      enqueueSnackbar(extrairMensagemErroCriacaoLote(error), {
        variant: "error",
      });
    } finally {
      setLoadingSalvarLote(false);
    }
  };

  const handleValorChange = (event) => {
    const valorLimpo = event.target.value.replace(/\D/g, "");

    if (!valorLimpo) {
      setValor("");
      return;
    }

    setValor(Number(valorLimpo) / 100);
  };

  const limparDados = () => {
    setProtocolo("");
    setPrevisao(null);
    setCompetencia("");
    setConvenio("");
    setDataInicial(criarDataInicial());
    setDataFinal(new Date());
    setValor("");
    setQuantidade("");
    setTipo("NORMAL");
    setLote("");
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleClose}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
    >
      <form onSubmit={salvarLote}>
        <DialogTitle
          id="scroll-dialog-title"
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="h6" fontWeight={700} color="text.primary">
                Construir lote
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Preencha os dados necessários para criar um novo lote.
              </Typography>
            </Box>
            <IconButton
              type="button"
              aria-label="Fechar modal"
              onClick={() => handleClose(false)}
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          id="scroll-dialog-description"
          sx={{ px: { xs: 2.5, sm: 3 }, py: 3 }}
        >
          <Box display="flex" flexDirection="column" gap={3}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ mb: 0.5 }}>
                Dados obrigatórios
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Estes campos precisam ser preenchidos para criar o lote.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={listaConvenios}
                    value={convenioSelecionado}
                    getOptionLabel={obterLabelConvenio}
                    filterOptions={filtrarConvenios}
                    isOptionEqualToValue={(option, value) =>
                      option?.convenio === value?.convenio
                    }
                    onChange={(_event, value) =>
                      setConvenio(value?.convenio || "")
                    }
                    fullWidth
                    renderInput={(params) => (
                      <TextField {...params} label="Convênio" required />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    id="lote"
                    label="Número do lote"
                    required
                    value={lote}
                    onChange={(e) => setLote(e.target.value)}
                    helperText={lote ? "Número sugerido; você pode alterá-lo." : " "}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    id="competencia"
                    placeholder="MM/AAAA"
                    required
                    label="Competência"
                    value={competencia}
                    onChange={(e) => setCompetencia(e.target.value)}
                    InputProps={{ inputComponent: MascaraDeCompetencia }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    id="tipo"
                    label="Tipo"
                    select
                    required
                    value={tipo}
                    onChange={(event) => setTipo(event.target.value)}
                    fullWidth
                  >
                    <MenuItem value="NORMAL">Normal</MenuItem>
                    <MenuItem value="INTERNO">Interno</MenuItem>
                    <MenuItem value="PENDENCIA">Pendência</MenuItem>
                    <MenuItem value="NEGOCIACAO">Negociação</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ mb: 0.5 }}>
                Informações adicionais
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Campos opcionais que podem ser preenchidos agora ou posteriormente.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="protocolo"
                    label="Protocolo"
                    value={protocolo}
                    onChange={(e) => setProtocolo(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="quantidade"
                    label="Quantidade de guias"
                    value={quantidade}
                    onChange={(e) => setQuantidade(e.target.value)}
                    inputProps={{ inputMode: "numeric" }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="valor"
                    label="Valor do lote"
                    value={formatarValorMonetario(valor)}
                    onChange={handleValorChange}
                    placeholder="0,00"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">R$</InputAdornment>
                      ),
                    }}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DataPickerAtualizado
                    data={previsao}
                    labelName="Data de previsão"
                    setData={setPrevisao}
                    fullWidth
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ mb: 0.5 }}>
                Período de envio
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Informe o período de envio para definir quais guias entram no lote.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ width: "100%" }}>
                    <DataPickerAtualizado
                      data={dataInicial}
                      labelName="Data inicial"
                      setData={setDataInicial}
                      slotProps={{ textField: { required: true } }}
                      fullWidth
                      sx={{ width: "100%" }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ width: "100%" }}>
                    <DataPickerAtualizado
                      data={dataFinal}
                      labelName="Data final"
                      setData={setDataFinal}
                      slotProps={{ textField: { required: true } }}
                      fullWidth
                      sx={{ width: "100%" }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
              <Typography variant="body2" color="text.secondary">
                Usuário responsável: <strong>{usuario || "Não informado"}</strong>
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "space-between",
          }}
        >
          <Button
            color="gray"
            variant="outlined"
            type="button"
            onClick={() => {
              handleClose(false);
              limparDados();
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="blue"
            disabled={loadingSalvarLote}
            type="submit"
            startIcon={loadingSalvarLote ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loadingSalvarLote ? "Criando lote..." : "Criar lote"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ModalConstruirLote;
